// © 2001-2015 INOVx Solutions, Inc.
// 114 Pacifica Suite 300, Irvine, CA 92618 USA
// All rights reserved.
//
// This software is confidential, proprietary and unpublished property of
// INOVx Solutions, Inc.  It may NOT be copied in part or in whole on any
// medium, either electronic or printed, without the express written consent
// of INOVx Solutions, Inc.
import * as BABYLON from "babylonjs";
import Hammer from "hammerjs";
import * as Enumerations from "../../Enumerations/Enumerations";
import * as Interfaces from "../../Interfaces/Interfaces";
import Collection from "../Structures/Collection";
let eventPrefix: string = "";

class UniversalCamera extends BABYLON.FreeCamera {

    public rotationSensitivityPos: number = 0; // maps to 0.5x..2x
    public velocitySensitivityPos: number = 0; // maps to 0.5x..5x

    // Rotation scaling
    public mouseRotateFactor = 1.5;
    public pinchFactor = 1;
    public mouseWheelFactor = .5;
    public mouseZoomFactor = .5;
    public _keysUp = [38, 87];    // Up Arrow, W
    public _keysDown = [40, 83];  // Down Arrow, S

    // use these for STRAFE (Q/E), not rotation
    public _keysStrafeLeft = [ 37, 65];   // Q
    public _keysStrafeRight = [ 39, 68];  // E

    // use these for ROTATE (A/D)
    public _keysRotateLeft = [81];   // A
    public _keysRotateRight = [ 69];  // D

    public _angularSensibilityX = 1000.0;
    public _angularSensibilityY = 1000.0;

    // Move/pan scaling
    public panningSensibility: number = 1; // larger = slower
    public panningSpeed: number = 3.0;
    public dollySensitivity = 5;

    // Inertia / offsets (rotation)
    public inertialAlphaOffset = 0; // yaw
    public inertialBetaOffset = 0; // pitch

    // Inertia / offsets (movement)
    public inertialMoveForward = 0; // forward/back along view direction
    public inertialMoveRight = 0; // strafe
    public inertialMoveUp = 0; // elevate

    // Limits (pitch)
    public lowerPitchLimit = -(Math.PI / 2) + 0.01;
    public upperPitchLimit = (Math.PI / 2) - 0.01;



    public cameraNavigationMode = Enumerations.CameraNavigationMode.Walk;

    // Movement tuning
    public moveSpeed = 12.5; 
    public zoomFactor = 1.0;

    private _invertY: boolean = false;
    private _invertX: boolean = false;
    private _timeSinceFrameUpdate = 0;

    // IMPORTANT: renamed from `_keys` to avoid colliding with Babylon internals
    private _pressedKeys: number[] = [];
    private _attachedElement: HTMLElement;

    public _sceneExtentZoomFactor = 1;

    private _mouseOver = false;
    private _clickCount: number = 0;
    private _viewerSelected: boolean = true;
    private _leftClickTime = 0;

    private _moving = false;

    // Fly mode treats the initial left press as the center of a virtual joystick.
    private _flyPointerStartX: number | null = null;
    private _flyPointerStartY: number | null = null;
    private _flyActiveButtons = 0;
    private _flyYawInput = 0;
    private _flyPitchInput = 0;
    private _flyForwardInput = 0;
    private _flyStrafeInput = 0;
    private _flyElevationInput = 0;
    private _flyKeepLevel = false;
    private readonly _flyDeadZonePixels = 4;
    private readonly _flyMaximumTranslationInput = 6;
    private readonly _flyMaximumYawRate = 0.5;
    private readonly _flyMaximumPitchRate = 0.5;

    private _onContextMenu: (e: PointerEvent) => void;
    private _onClick: (e: PointerEvent) => void;
    private _onPointerDown: (e: PointerEvent) => void;
    private _onPointerUp: (e: PointerEvent) => void;
    private _onPointerMove: (e: PointerEvent) => void;
    private _wheel: (e: BABYLON.IMouseEvent) => void;
    private _onMouseMove: (e: MouseEvent) => any;
    private _onKeyDown: (e: KeyboardEvent) => any;
    private _onKeyUp: (e: KeyboardEvent) => any;
    private _onFlyModifierKeyDown: (e: KeyboardEvent) => any;
    private _onFlyModifierKeyUp: (e: KeyboardEvent) => any;
    private _onLostFocus: (e: FocusEvent) => any;
    private _onElementClicked: (e: MouseEvent) => any;
    private _onBodyClicked: (e: MouseEvent) => any;
    private _onMouseLeave: (e: DragEvent) => any;
    private _onMouseEnter: (e: DragEvent) => any;
    declare public _reset: () => void;

    private _wheelObs: any;

    declare public _localDirection: BABYLON.Vector3;
    declare public _transformedDirection: BABYLON.Vector3;

    private _headlight: BABYLON.DirectionalLight;
    private _eventHandler: Interfaces.Interfaces.IEventManager;

    // When true, user input that would rotate/pan/zoom is ignored (used while gizmos or other UI drag operations are active)
    private _suspendControls: boolean = false;

    // Hammer
    private _hammerManager: HammerManager | null = null;

    public get angularSensibilityX() {
        return this._angularSensibilityX;
    }
    public get angularSensibilityY() {
        return this._angularSensibilityY;
    }

    public focusInput() {
        this._viewerSelected = true;
        this.getEngine().getRenderingCanvas()?.focus();
    }

    constructor(
        name: string,
        eventHander: Interfaces.Interfaces.IEventManager,
        position: BABYLON.Vector3,
        scene: BABYLON.Scene
    ) {
        super(name, position.clone(), scene);

        eventPrefix = BABYLON.Tools.GetPointerPrefix(scene.getEngine());
        this._eventHandler = eventHander;

        // We manage our own interactions
        this.inputs.clear();

        // Camera feel
        this.inertia = 0;
        this.minZ = 0.1;

        // Fire camera changed + update headlight based on view changes
        this.onViewMatrixChangedObservable.add(() => {
            // Headlight
            if (this._headlight) {
                const lightPosition = this.position.clone();
                this._headlight.direction = this.getTarget().subtract(this.position).normalize();
                this._headlight.position = lightPosition;
            }

            // Event payload (matches your ExamineCamera pattern)
            const target = this.getTarget();
            const cameraDirection = target.subtract(this.position).normalize();
            const directionVector = new BABYLON.Vector3(cameraDirection.x, 0, cameraDirection.z).normalize();

            let upDotProduct = BABYLON.Vector3.Dot(cameraDirection, directionVector);
            let directionDotProduct = BABYLON.Vector3.Dot(directionVector, new BABYLON.Vector3(0, 0, 1));

            upDotProduct = upDotProduct > 1 ? 1 : upDotProduct;
            directionDotProduct = directionDotProduct > 1 ? 1 : directionDotProduct;
            upDotProduct = upDotProduct < -1 ? -1 : upDotProduct;
            directionDotProduct = directionDotProduct < -1 ? -1 : directionDotProduct;

            const angleUp = Math.acos(upDotProduct);
            let angleFromNorth = Math.acos(directionDotProduct);

            // If the X is negative, then the rotation is 360 degrees MINUS the angle.
            angleFromNorth = directionVector.x < 0 ? 2 * Math.PI - angleFromNorth : angleFromNorth;

            const upAxis =
                Math.round((angleUp * 180) / Math.PI) === 0 ? "" : cameraDirection.y > 0 ? "U" : "D";

            const rotatedDegrees =
                Math.round((angleFromNorth * 180) / Math.PI) === 360
                    ? 0
                    : Math.round((angleFromNorth * 180) / Math.PI);

            const upDegrees = (angleUp * 180) / Math.PI > 89 ? 90 : Math.round((angleUp * 180) / Math.PI);

            const args: any = {
                rotatedDegrees: rotatedDegrees,
                upDegrees: upDegrees + "° " + upAxis,
                pos: this.position,
                target: target,
            };

            this._eventHandler.fireEvent(Enumerations.EventType.CAMERACHANGED, args);
        });

        this._timeSinceFrameUpdate = BABYLON.Tools.Now;
        this.fov = .96;
    }

    public computeViewMatrix(force?: boolean) {
        this._computeViewMatrix(this.position, this.target, this.upVector);
    }

    public updateZoomSpeed(_bounds: BABYLON.BoundingBox) { }

    public disposeHeadlights() {
        if (this._headlight) {
            this._scene.removeLight(this._headlight);
            this._headlight.dispose();
            this._headlight = null;
        }
    }

    public hasHeadlights() {
        return this._headlight;
    }

    public setHeadlight(light: BABYLON.DirectionalLight) {
        if (this._headlight) this._headlight.dispose();
        light.position = this.position;
        this._headlight = light;
    }

    public isMoving(): boolean {
        return (
            this._moving ||
            this.inertialAlphaOffset !== 0 ||
            this.inertialBetaOffset !== 0 ||
            this.inertialMoveForward !== 0 ||
            this.inertialMoveRight !== 0 ||
            this.inertialMoveUp !== 0 ||
            this._pressedKeys.length > 0
        );
    }

    /**
     * Temporarily suspend camera interactive controls (pointer rotate, pan, zoom, wheel, touch).
     * Call with true on gizmo drag start and false on drag end.
     */
    public setControlsSuspended(suspended: boolean, resetInertia: boolean = true) {
        this._suspendControls = suspended;
        if (suspended) {
            this._clearFlyInput();
        }
        if (suspended && resetInertia) {
            this.inertialAlphaOffset = 0;
            this.inertialBetaOffset = 0;
            this.inertialMoveForward = 0;
            this.inertialMoveRight = 0;
            this.inertialMoveUp = 0;
        }
    }

    public setNavigationMode(mode: Enumerations.CameraNavigationMode) {
        this.cameraNavigationMode = mode;
        this._clearFlyInput();
        this.inertialAlphaOffset = 0;
        this.inertialBetaOffset = 0;
        this.inertialMoveForward = 0;
        this.inertialMoveRight = 0;
        this.inertialMoveUp = 0;
    }

    private _isFlyNavigationMode(): boolean {
        return this.cameraNavigationMode === Enumerations.CameraNavigationMode.Fly;
    }

    private _beginFlyInput(clientX: number, clientY: number) {
        this._flyPointerStartX = clientX;
        this._flyPointerStartY = clientY;
        this._flyYawInput = 0;
        this._flyPitchInput = 0;
        this._flyForwardInput = 0;
        this._flyStrafeInput = 0;
        this._flyElevationInput = 0;
        this._timeSinceFrameUpdate = 0;
    }

    private _clearFlyInput() {
        this._flyPointerStartX = null;
        this._flyPointerStartY = null;
        this._flyActiveButtons = 0;
        this._flyYawInput = 0;
        this._flyPitchInput = 0;
        this._flyForwardInput = 0;
        this._flyStrafeInput = 0;
        this._flyElevationInput = 0;
    }

    private _scaleFlyRotationDisplacement(displacement: number, referenceDisplacement: number): number {
        const magnitude = Math.abs(displacement);
        if (magnitude <= this._flyDeadZonePixels) return 0;

        const usableRange = Math.max(1, referenceDisplacement - this._flyDeadZonePixels);
        const scaled = (magnitude - this._flyDeadZonePixels) / usableRange;
        return Math.sign(displacement) * scaled;
    }

    private _scaleFlyTranslationDisplacement(displacement: number, referenceDisplacement: number): number {
        const linearInput = this._scaleFlyRotationDisplacement(displacement, referenceDisplacement);
        const magnitude = Math.abs(linearInput);
        if (magnitude <= 1) return linearInput;

        const excess = magnitude - 1;
        const acceleratedInput = 1 + excess + 0.5 * excess * excess;
        return Math.sign(linearInput) * Math.min(this._flyMaximumTranslationInput, acceleratedInput);
    }

    private _clearFlyRates() {
        this._flyYawInput = 0;
        this._flyPitchInput = 0;
        this._flyForwardInput = 0;
        this._flyStrafeInput = 0;
        this._flyElevationInput = 0;
    }

    private _updateFlyInput(clientX: number, clientY: number, element: HTMLElement) {
        if (this._flyPointerStartX === null || this._flyPointerStartY === null) {
            this._beginFlyInput(clientX, clientY);
            return;
        }

        const yawRange = Math.max(1, element.clientWidth * 0.25);
        const movementRange = Math.max(1, element.clientHeight * 0.25);

        this._flyPitchInput = 0;
        this._flyStrafeInput = 0;
        this._flyElevationInput = 0;
        this._flyYawInput = this._scaleFlyRotationDisplacement(clientX - this._flyPointerStartX, yawRange);
        this._flyForwardInput = this._scaleFlyTranslationDisplacement(this._flyPointerStartY - clientY, movementRange);
    }

    private _updateFlyStrafeInput(clientX: number, clientY: number, element: HTMLElement) {
        if (this._flyPointerStartX === null || this._flyPointerStartY === null) {
            this._beginFlyInput(clientX, clientY);
            return;
        }

        const horizontalRange = Math.max(1, element.clientWidth * 0.25);
        const verticalRange = Math.max(1, element.clientHeight * 0.25);

        this._flyYawInput = 0;
        this._flyPitchInput = 0;
        this._flyForwardInput = 0;
        this._flyStrafeInput = this._scaleFlyTranslationDisplacement(clientX - this._flyPointerStartX, horizontalRange);
        this._flyElevationInput = this._scaleFlyTranslationDisplacement(this._flyPointerStartY - clientY, verticalRange);
    }

    private _updateFlyRotationInput(clientX: number, clientY: number, element: HTMLElement) {
        if (this._flyPointerStartX === null || this._flyPointerStartY === null) {
            this._beginFlyInput(clientX, clientY);
            return;
        }

        const yawRange = Math.max(1, element.clientWidth * 0.25);
        const pitchRange = Math.max(1, element.clientHeight * 0.25);

        this._flyForwardInput = 0;
        this._flyStrafeInput = 0;
        this._flyElevationInput = 0;
        this._flyYawInput = this._scaleFlyRotationDisplacement(clientX - this._flyPointerStartX, yawRange);
        this._flyPitchInput = this._scaleFlyRotationDisplacement(clientY - this._flyPointerStartY, pitchRange);
    }

    private _setFlyActiveButtons(buttons: number) {
        this._flyActiveButtons = buttons;
    }

    private _updateActiveFlyGesture(buttons: number, clientX: number, clientY: number, element: HTMLElement) {
        if (buttons === 1) {
            this._updateFlyInput(clientX, clientY, element);
        } else if (buttons === 2) {
            this._updateFlyRotationInput(clientX, clientY, element);
        } else if (buttons === 4) {
            this._updateFlyStrafeInput(clientX, clientY, element);
        } else {
            // Mouse-button chords intentionally have no Fly behavior.
            this._clearFlyRates();
        }
    }

    public attachControl(element: any, preventDefault?: boolean, useCtrlForPanning: boolean = false): void {
        const that = this;

        element = this.getEngine().getRenderingCanvas() as any;
        if (!element) return;

        element.tabIndex = element.tabIndex || 1;
        element.focus();

        if (useCtrlForPanning === void 0) useCtrlForPanning = false;

        // Ensure canvas can receive keyboard events
        element.tabIndex = element.tabIndex || 1;
        try {
            element.focus();
        } catch { }

        let cacheSoloPointer: any;
        let previousPinchDistance = 0;
        let previousPinchDirection = 0;
        let panCount: any;
        let previousRotate: any;
        let previousPan: any;
        let previousPan1: any;
        let previousPan2: any;

        const pointers = new Collection();

        if (that._attachedElement) return;
        that._attachedElement = element;

        const engine = that.getEngine();

        if (that._onPointerDown === undefined) {
            that._onPointerDown = function (evt: PointerEvent) {
                that._flyKeepLevel = evt.shiftKey;
                if ((evt as any).buttons === 1) that._leftClickTime = (evt as any).timeStamp;
                else that._leftClickTime = 0;

                if (
                    that._isFlyNavigationMode() &&
                    (((evt as any).buttons & 7) !== 0)
                ) {
                    const buttons = (evt as any).buttons;
                    const startingFresh = that._flyActiveButtons === 0;

                    if (startingFresh) {
                        that._beginFlyInput((evt as any).clientX, (evt as any).clientY);
                    }

                    that._setFlyActiveButtons(buttons);

                    if (!startingFresh) {
                        that._updateActiveFlyGesture(
                            buttons,
                            (evt as any).clientX,
                            (evt as any).clientY,
                            element
                        );
                    }
                }

                that._moving = true;

                // manage pointers
                pointers.add((evt as any).pointerId, {
                    x: (evt as any).clientX,
                    y: (evt as any).clientY,
                    type: (evt as any).pointerType,
                });
                cacheSoloPointer = pointers.item((evt as any).pointerId);

                if (preventDefault) evt.preventDefault();
            };

            that._onPointerUp = function (evt: PointerEvent) {
                const buttons = (evt as any).buttons;
                if (buttons === 0 || evt.type === eventPrefix + "out") {
                    that._moving = false;
                    that._clearFlyInput();
                    cacheSoloPointer = null;
                    previousPinchDistance = 0;
                    pointers.remove((evt as any).pointerId);
                } else if (
                    that._isFlyNavigationMode() &&
                    ((buttons & 7) !== 0)
                ) {
                    // Preserve the existing anchor until every mouse button is released.
                    that._setFlyActiveButtons(buttons);
                    that._updateActiveFlyGesture(
                        buttons,
                        (evt as any).clientX,
                        (evt as any).clientY,
                        element
                    );
                }
                if (preventDefault) evt.preventDefault();
            };

            that._onContextMenu = function (evt: PointerEvent) {
                evt.preventDefault();
            };

            that._onClick = function (evt: PointerEvent) {
                if (((evt as any).timeStamp - that._leftClickTime) < 250) {
                    let clickEvent: any;
                    try {
                        clickEvent = new MouseEvent("press", { screenX: (evt as any).x, screenY: (evt as any).y });
                    } catch (e) {
                        clickEvent = document.createEvent("MouseEvent");
                        clickEvent.initMouseEvent(
                            "press",
                            true,
                            true,
                            window,
                            0,
                            (evt as any).clientX,
                            (evt as any).clientY,
                            0,
                            0,
                            false,
                            false,
                            false,
                            false,
                            0,
                            (evt as any).target
                        );
                    }
                    window.dispatchEvent(clickEvent);
                }
            };

            that._onPointerMove = function (evt: PointerEvent) {
                if (preventDefault) evt.preventDefault();
                that._flyKeepLevel = evt.shiftKey;

                if ((evt as any).pointerType !== "touch") {
                    if (that._suspendControls) {
                        // Keep cached pointer position in sync while controls are suspended so resuming does not apply a stale delta
                        if (pointers.item((evt as any).pointerId)) {
                            const cp = pointers.item((evt as any).pointerId);
                            cp.x = (evt as any).clientX;
                            cp.y = (evt as any).clientY;
                        }
                        return;
                    }

                    // make sure the evt id is actually remembered as being DOWN
                    if (pointers.item((evt as any).pointerId) == null) return;

                    switch (pointers.count) {
                        case 1: {
                            const buttons = (evt as any).buttons;

                            if (that._isFlyNavigationMode()) {
                                if (
                                    ((buttons & 7) !== 0) &&
                                    buttons !== that._flyActiveButtons
                                ) {
                                    // Pointer Events may not emit another pointerdown when a second
                                    // mouse button joins an existing gesture. Change the active mapping,
                                    // but preserve the anchor for the lifetime of the mouse-button chord.
                                    that._setFlyActiveButtons(buttons);
                                }

                                if ((buttons & 7) !== 0) {
                                    that._updateActiveFlyGesture(
                                        buttons,
                                        (evt as any).clientX,
                                        (evt as any).clientY,
                                        element
                                    );
                                } else {
                                    that._clearFlyInput();
                                }
                                break;
                            }

                            if (buttons === 4) {

                                const scale = Math.max(1, that.moveSpeed) * 0.01;

                                const dx = (evt as any).clientX - cacheSoloPointer.x;
                                const dy = (evt as any).clientY - cacheSoloPointer.y;

                                that.inertialMoveRight += -(dx / that.panningSensibility) * scale;
                                that.inertialMoveUp += (dy / that.panningSensibility) * scale;
                            }
                            // LMB => look
                            else if (buttons === 1) {
                                const offsetX = (evt as any).clientX - cacheSoloPointer.x;
                                const offsetY = (evt as any).clientY - cacheSoloPointer.y;

                                const rot = that.mouseRotateFactor;
                                const rotMul = that._rotationMultiplier();

                                if (that._invertY) {
                                    that.inertialBetaOffset -= (offsetY * rotMul / that.angularSensibilityY) * rot;
                                } else {
                                    that.inertialBetaOffset += (offsetY * rotMul / that.angularSensibilityY) * rot;
                                }

                                if (that._invertX) {
                                    that.inertialAlphaOffset -= (offsetX * rotMul / that.angularSensibilityX) * rot;
                                } else {
                                    that.inertialAlphaOffset += (offsetX * rotMul / that.angularSensibilityX) * rot;
                                }
                            }
                            // RMB+LMB (buttons==5) => dolly forward/back
                            else if (buttons === 5) {
                                const offsetY = (evt as any).clientY - cacheSoloPointer.y;

                                const zoomFactor = Math.max(0.05, that.moveSpeed);
                                const velMul = that._velocityMultiplier();

                                that.inertialMoveForward += (offsetY * velMul / that.dollySensitivity) * zoomFactor;
                                that.inertialMoveForward *= that.mouseZoomFactor;
                            }

                            cacheSoloPointer.x = (evt as any).clientX;
                            cacheSoloPointer.y = (evt as any).clientY;
                            break;
                        }
                        case 2: {
                            pointers.item((evt as any).pointerId).x = (evt as any).clientX;
                            pointers.item((evt as any).pointerId).y = (evt as any).clientY;

                            const direction = that.pinchFactor;

                            const distX = pointers.getItemByIndex(0).x - pointers.getItemByIndex(1).x;
                            const distY = pointers.getItemByIndex(0).y - pointers.getItemByIndex(1).y;
                            const pinchSquaredDistance = (distX * distX) + (distY * distY);

                            if (previousPinchDistance === 0) {
                                previousPinchDistance = pinchSquaredDistance;
                                return;
                            }

                            if (pinchSquaredDistance !== previousPinchDistance) {
                                const zoomFactor = Math.max(0.05, that.moveSpeed);
                                const velMul = that._velocityMultiplier();
                                that.inertialMoveForward +=
                                    ((pinchSquaredDistance - previousPinchDistance) / (that.dollySensitivity * 10000)) * zoomFactor * velMul;
                                that.inertialMoveForward *= direction;
                                previousPinchDistance = pinchSquaredDistance;
                            }
                            break;
                        }
                        default: {
                            if (pointers.item((evt as any).pointerId)) {
                                pointers.item((evt as any).pointerId).x = (evt as any).clientX;
                                pointers.item((evt as any).pointerId).y = (evt as any).clientY;
                            }
                            break;
                        }
                    }
                }
            };

            that._onMouseMove = function (evt: MouseEvent) {
                if (!engine.isPointerLock) return;
                if (that._suspendControls) return;
                if (that._isFlyNavigationMode()) return;

                const offsetX =
                    (evt as any).movementX ||
                    (evt as any).mozMovementX ||
                    (evt as any).webkitMovementX ||
                    (evt as any).msMovementX ||
                    0;
                const offsetY =
                    (evt as any).movementY ||
                    (evt as any).mozMovementY ||
                    (evt as any).webkitMovementY ||
                    (evt as any).msMovementY ||
                    0;

                const rot = that.mouseRotateFactor;
                const rotMul = that._rotationMultiplier();

                if (that._invertY) {
                    that.inertialBetaOffset -= (offsetY * rotMul / that.angularSensibilityY) * rot;
                } else {
                    that.inertialBetaOffset += (offsetY * rotMul / that.angularSensibilityY) * rot;
                }

                if (that._invertX) {
                    that.inertialAlphaOffset -= (offsetX * rotMul / that.angularSensibilityX) * rot;
                } else {
                    that.inertialAlphaOffset += (offsetX * rotMul / that.angularSensibilityX) * rot;
                }
                if (preventDefault) evt.preventDefault();
            };

            that._wheel = function (event: any) {
                if (that._suspendControls) return;

                if (that._isFlyNavigationMode()) {
                    that._flyKeepLevel = !!event.shiftKey;
                }

                let delta = 0;
                const wheelDelta: number = (event as any).wheelDelta;

                if (wheelDelta) delta = wheelDelta / (3.0 * 40);
                else if ((event as any).detail) delta = -(event as any).detail / 3.0;
                else if ((event as any).deltaY) delta = -(event as any).deltaY / 100;

                if (delta) {
                    const zoomFactor = Math.max(0.05, that.moveSpeed);
                    const velMul = that._velocityMultiplier();

                    that.inertialMoveForward += (delta / that.dollySensitivity) * zoomFactor * velMul;
                    that.inertialMoveForward *= that.mouseWheelFactor;
                }

                if (event.preventDefault && preventDefault) event.preventDefault();
            };

            that._onKeyDown = function (evt: KeyboardEvent) {
                if (!that._viewerSelected) return;

                that._flyKeepLevel = evt.shiftKey || evt.keyCode === 16;

                const isMoveKey =
                    that._keysUp.indexOf(evt.keyCode) !== -1 ||
                    that._keysDown.indexOf(evt.keyCode) !== -1 ||
                    that._keysStrafeLeft.indexOf(evt.keyCode) !== -1 ||
                    that._keysStrafeRight.indexOf(evt.keyCode) !== -1 ||
                    that._keysRotateLeft.indexOf(evt.keyCode) !== -1 ||
                    that._keysRotateRight.indexOf(evt.keyCode) !== -1;

                if (!isMoveKey) return;

                const idx = that._pressedKeys.indexOf(evt.keyCode);
                if (idx === -1) {
                    // starting input -> prime dt next frame
                    that._timeSinceFrameUpdate = 0;
                    that._pressedKeys.push(evt.keyCode);
                }

                if (evt.preventDefault && preventDefault) evt.preventDefault();
            };


            that._onKeyUp = function (evt: KeyboardEvent) {
                that._flyKeepLevel = evt.keyCode === 16 ? false : evt.shiftKey;
                const index = that._pressedKeys.indexOf(evt.keyCode);
                if (index >= 0) that._pressedKeys.splice(index, 1);

                if (evt.preventDefault && preventDefault) evt.preventDefault();
            };

            that._onFlyModifierKeyDown = function (evt: KeyboardEvent) {
                if (
                    that._flyActiveButtons !== 0 &&
                    (evt.key === "Shift" || evt.keyCode === 16)
                ) {
                    that._flyKeepLevel = true;
                }
            };

            that._onFlyModifierKeyUp = function (evt: KeyboardEvent) {
                if (evt.key === "Shift" || evt.keyCode === 16) {
                    that._flyKeepLevel = false;
                }
            };

            that._onMouseLeave = function (evt: DragEvent) {
                evt.preventDefault();
                that._mouseOver = false;
            };
            that._onMouseEnter = function (_evt: DragEvent) {
                that._mouseOver = true;
            };

            that._onElementClicked = function () {
                that._clickCount = 1;
            };

            that._onBodyClicked = function (evt: MouseEvent) {
                that._clickCount++;

                if (that._clickCount == 2) {
                    that._viewerSelected = true;
                    evt.preventDefault();
                } else {
                    that._viewerSelected = false;
                }

                that._clickCount = 0;
            };

            that._onLostFocus = function () {
                that._pressedKeys = [];
                that._flyKeepLevel = false;
                that._moving = false;
                that._clearFlyInput();
                pointers.empty();
                previousPinchDistance = 0;
                cacheSoloPointer = null;
            };

            that._reset = function () {
                that._pressedKeys = [];
                that._flyKeepLevel = false;
                that._moving = false;
                that._clearFlyInput();
                that.inertialAlphaOffset = 0;
                that.inertialBetaOffset = 0;
                that.inertialMoveForward = 0;
                that.inertialMoveRight = 0;
                that.inertialMoveUp = 0;
                pointers.empty();
                previousPinchDistance = 0;
                cacheSoloPointer = null;
            };
        }

        if (!useCtrlForPanning) {
            element.addEventListener("contextmenu", that._onContextMenu, false);
        }

        // Hammer touch gestures
        if (this._hammerManager) {
            try {
                this._hammerManager.destroy();
            } catch { }
            this._hammerManager = null;
        }

        const mc = new Hammer.Manager(element, {
            recognizers: [[Hammer.Pan, { event: "pan", pointers: 1 }]],
        });

        mc.get("pan").set({ direction: Hammer.DIRECTION_ALL, threshold: 1, pointers: 0 });

        mc.on("panstart pinchstart", function (ev: any) {
            previousPinchDistance = 0;
            panCount = ev.pointers.length;
            that._moving = true;

            if (that._suspendControls) return;

            if (ev.pointerType == "touch") {
                if (ev.pointers.length == 1) {
                    previousRotate = ev.pointers[0];
                } else if (ev.pointers.length == 2) {
                    previousPan = ev.pointers[0];
                    previousPan1 = ev.pointers[0];
                    previousPan2 = ev.pointers[1];
                }
            }
        });

        mc.on("panend pinchend", function () {
            that._moving = false;
            that._clearFlyInput();
        });

        mc.on("panmove pinchmove", function (ev: any) {
            if (preventDefault) ev.preventDefault();
            if (that._suspendControls) return;

            if (ev.pointerType == "touch") {
                if (ev.pointers.length == 1) {
                    if (that._isFlyNavigationMode()) {
                        that._updateFlyInput(ev.pointers[0].clientX, ev.pointers[0].clientY, element);
                        return;
                    }

                    if (panCount != ev.pointers.length) {
                        previousRotate = ev.pointers[0];
                        panCount = ev.pointers.length;
                    }

                    const offsetX = ev.pointers[0].clientX - previousRotate.clientX;
                    const offsetY = ev.pointers[0].clientY - previousRotate.clientY;

                    const rotMul = that._rotationMultiplier();

                    that.inertialAlphaOffset -= offsetX * rotMul / 300;
                    that.inertialBetaOffset -= offsetY * rotMul / 300;

                    previousRotate = ev.pointers[0];
                } else if (ev.pointers.length == 2) {
                    if (panCount != ev.pointers.length) {
                        previousPan = ev.pointers[0];
                        previousPan1 = ev.pointers[0];
                        previousPan2 = ev.pointers[1];
                        panCount = ev.pointers.length;
                    }

                    // Two-finger pan => strafe + elevate
                    const yPanChange =
                        (ev.pointers[0].clientY - previousPan1.clientY) + (ev.pointers[1].clientY - previousPan2.clientY);
                    const xPanChange =
                        (ev.pointers[0].clientX - previousPan1.clientX) + (ev.pointers[1].clientX - previousPan2.clientX);

                    const xPanning =
                        ((ev.pointers[0].clientX - previousPan1.clientX) < 0 && (ev.pointers[1].clientX - previousPan2.clientX) < 0) ||
                        ((ev.pointers[0].clientX - previousPan1.clientX) > 0 && (ev.pointers[1].clientX - previousPan2.clientX) > 0);

                    const yPanning =
                        ((ev.pointers[0].clientY - previousPan1.clientY) < 0 && (ev.pointers[1].clientY - previousPan2.clientY) < 0) ||
                        ((ev.pointers[0].clientY - previousPan1.clientY) > 0 && (ev.pointers[1].clientY - previousPan2.clientY) > 0);

                    // Ignore tiny deltas so we don't pan while pinching
                    const moveScale = Math.max(1, that.moveSpeed) * 0.01;

                    if ((xPanChange > 1 || xPanChange < -1) && xPanning) {
                        that.inertialMoveRight += -((xPanChange / 2) / that.panningSensibility) * moveScale;

                        previousPan = ev.pointers[0];
                        previousPan1 = ev.pointers[0];
                        previousPan2 = ev.pointers[1];
                    }

                    if ((yPanChange > 2 || yPanChange < -2) && yPanning) {
                        that.inertialMoveUp += ((yPanChange / 2) / that.panningSensibility) * moveScale;

                        previousPan = ev.pointers[0];
                        previousPan1 = ev.pointers[0];
                        previousPan2 = ev.pointers[1];
                    }

                    // Pinch => dolly forward/back
                    const distX = ev.pointers[0].clientX - ev.pointers[1].clientX;
                    const distY = ev.pointers[0].clientY - ev.pointers[1].clientY;
                    const pinchSquaredDistance = (distX * distX) + (distY * distY);

                    if (previousPinchDistance === 0) {
                        previousPinchDistance = pinchSquaredDistance;
                        return;
                    }

                    if (pinchSquaredDistance !== previousPinchDistance) {
                        let pinchThreshold =
                            ((previousPinchDirection > 0 && (pinchSquaredDistance - previousPinchDistance) > 0) ||
                                (previousPinchDirection < 0 && (pinchSquaredDistance - previousPinchDistance) < 0)) ||
                                previousPinchDirection == 0
                                ? 1
                                : 35;

                        pinchThreshold =
                            Math.abs(that.inertialMoveRight) > 0.001 || Math.abs(that.inertialMoveUp) > 0.001 ? 35 : pinchThreshold;

                        const pinchSpeed = Math.max(0.05, that.moveSpeed);
                        const velMul = that._velocityMultiplier();

                        if (Math.abs(Math.sqrt(pinchSquaredDistance) - Math.sqrt(previousPinchDistance)) > pinchThreshold) {
                            that.inertialMoveForward +=
                                pinchSpeed *
                                ((pinchSquaredDistance - previousPinchDistance) / pinchThreshold) /
                                (100000 * that.zoomFactor);

                            that.inertialMoveForward *= velMul;
                            that.inertialMoveForward *= that.pinchFactor;

                            previousPinchDirection = pinchSquaredDistance - previousPinchDistance;
                            previousPinchDistance = pinchSquaredDistance;
                        }
                    }
                }
            }
        });

        this._hammerManager = mc;

        // DOM events
        element.addEventListener(eventPrefix + "down", that._onPointerDown, false);
        element.addEventListener(eventPrefix + "up", that._onPointerUp, false);
        element.addEventListener(eventPrefix + "out", that._onPointerUp, false);
        element.addEventListener(eventPrefix + "move", that._onPointerMove, false);
        element.addEventListener("mousemove", that._onMouseMove, false);
        element.addEventListener("click", that._onClick, false);

        that._wheelObs = that._scene.onPointerObservable.add(
            (eventData) => {
                that._wheel(eventData.event);
            },
            BABYLON.PointerEventTypes.POINTERWHEEL
        );

        element.addEventListener("keydown", that._onKeyDown, false);
        element.addEventListener("keyup", that._onKeyUp, false);
        element.addEventListener("blur", that._onLostFocus, false);
        window.addEventListener("keydown", that._onFlyModifierKeyDown, true);
        window.addEventListener("keyup", that._onFlyModifierKeyUp, true);
        window.addEventListener("blur", that._onLostFocus, false);

        element.addEventListener("mouseenter", that._onMouseEnter, false);
        element.addEventListener("mouseleave", that._onMouseLeave, false);

        element.addEventListener("click", that._onElementClicked, false);
        document.body.addEventListener("click", that._onBodyClicked, false);
    }

    public detachControl(element?: any): void {
        if (!this._attachedElement) return;

        element = element || this._attachedElement;

        element.removeEventListener("contextmenu", this._onContextMenu);
        element.removeEventListener("click", this._onClick);

        element.removeEventListener(eventPrefix + "down", this._onPointerDown);
        element.removeEventListener(eventPrefix + "up", this._onPointerUp);
        element.removeEventListener(eventPrefix + "out", this._onPointerUp);
        element.removeEventListener(eventPrefix + "move", this._onPointerMove);
        element.removeEventListener("mousemove", this._onMouseMove);

        element.removeEventListener("keydown", this._onKeyDown, false);
        element.removeEventListener("keyup", this._onKeyUp, false);
        element.removeEventListener("blur", this._onLostFocus, false);
        window.removeEventListener("keydown", this._onFlyModifierKeyDown, true);
        window.removeEventListener("keyup", this._onFlyModifierKeyUp, true);
        window.removeEventListener("blur", this._onLostFocus, false);

        element.removeEventListener("mouseenter", this._onMouseEnter, false);
        element.removeEventListener("mouseleave", this._onMouseLeave, false);

        this._scene.onPointerObservable.remove(this._wheelObs);

        element.removeEventListener("click", this._onElementClicked, false);
        document.body.removeEventListener("click", this._onBodyClicked, false);

        if (this._hammerManager) {
            try {
                this._hammerManager.destroy();
            } catch { }
            this._hammerManager = null;
        }

        this._attachedElement = null;

        if (this._reset) this._reset();
    }

    public _checkInputs(): void {

        const now = BABYLON.Tools.Now;

        // We skip the first 0 frame render. This prevents large jumps on the first key press.
        if (this._timeSinceFrameUpdate === 0) {
            this._timeSinceFrameUpdate = now;
            super._checkInputs.call(this);
            return;
        }

        let dt = (now - this._timeSinceFrameUpdate) / 1000;

        const MAX_DT = 0.05;
        if (dt > MAX_DT) dt = MAX_DT;
        if (dt < 0) dt = 0;

        this._timeSinceFrameUpdate = now;

        const keyImpulse = 30.0 * dt;      // move impulse
        const rotateImpulse = 1.0 * dt;    // tune this (radians/sec-ish)

        if (this._isFlyNavigationMode()) {
            const rotMul = this._rotationMultiplier();
            const velMul = this._velocityMultiplier();
            const forwardInput = this._flyForwardInput;

            this.rotation.y += this._flyYawInput * this._flyMaximumYawRate * rotMul * dt;
            this.rotation.x += this._flyPitchInput * this._flyMaximumPitchRate * rotMul * dt;

            if (this.rotation.x < this.lowerPitchLimit) this.rotation.x = this.lowerPitchLimit;
            if (this.rotation.x > this.upperPitchLimit) this.rotation.x = this.upperPitchLimit;

            if (forwardInput !== 0) {
                const forward = this.getDirection(BABYLON.Axis.Z);

                if (this._flyKeepLevel) {
                    forward.y = 0;
                }

                if (forward.lengthSquared() <= BABYLON.Epsilon) {
                    forward.copyFromFloats(Math.sin(this.rotation.y), 0, Math.cos(this.rotation.y));
                } else {
                    forward.normalize();
                }

                const distance = forwardInput * this.moveSpeed * this.zoomFactor * velMul * dt;
                this.position.addInPlace(forward.scale(distance));
            }

            if (this._flyStrafeInput !== 0 || this._flyElevationInput !== 0) {
                const right = this.getDirection(BABYLON.Axis.X);
                right.y = 0;

                if (right.lengthSquared() <= BABYLON.Epsilon) {
                    right.copyFromFloats(Math.cos(this.rotation.y), 0, -Math.sin(this.rotation.y));
                } else {
                    right.normalize();
                }

                const speed = this.moveSpeed * this.zoomFactor * velMul * dt;
                this.position.addInPlace(right.scale(this._flyStrafeInput * speed));
                this.position.y += this._flyElevationInput * speed;
            }
        }

        if (this._isFlyNavigationMode()) {
            const keyboardRotation = this._flyMaximumYawRate * this._rotationMultiplier() * dt;

            for (let i = 0; i < this._pressedKeys.length; i++) {
                const keyCode = this._pressedKeys[i];

                if (keyCode === 37 || keyCode === 65) this.rotation.y -= keyboardRotation;
                else if (keyCode === 39 || keyCode === 68) this.rotation.y += keyboardRotation;
                else if (keyCode === 38 || keyCode === 87) this.rotation.x -= keyboardRotation;
                else if (keyCode === 40 || keyCode === 83) this.rotation.x += keyboardRotation;
            }

            if (this.rotation.x < this.lowerPitchLimit) this.rotation.x = this.lowerPitchLimit;
            if (this.rotation.x > this.upperPitchLimit) this.rotation.x = this.upperPitchLimit;
        } else {
            for (let i = 0; i < this._pressedKeys.length; i++) {
                const keyCode = this._pressedKeys[i];
                const rotMul = this._rotationMultiplier();
                const velMul = this._velocityMultiplier();

                // forward/back
                if (this._keysUp.indexOf(keyCode) !== -1) this.inertialMoveForward += keyImpulse * velMul;
                else if (this._keysDown.indexOf(keyCode) !== -1) this.inertialMoveForward -= keyImpulse * velMul;

                // Q/E strafing
                else if (this._keysStrafeLeft.indexOf(keyCode) !== -1) this.inertialMoveRight -= keyImpulse * velMul;
                else if (this._keysStrafeRight.indexOf(keyCode) !== -1) this.inertialMoveRight += keyImpulse * velMul;

                // A/D rotation (yaw)
                else if (this._keysRotateLeft.indexOf(keyCode) !== -1) this.inertialAlphaOffset -= rotateImpulse * rotMul;
                else if (this._keysRotateRight.indexOf(keyCode) !== -1) this.inertialAlphaOffset += rotateImpulse * rotMul;
            }
        }


        if (
            this.inertialAlphaOffset !== 0 ||
            this.inertialBetaOffset !== 0 ||
            this.inertialMoveForward !== 0 ||
            this.inertialMoveRight !== 0 ||
            this.inertialMoveUp !== 0
        ) {
            // Rotation (yaw/pitch)
            this.rotation.y += this.inertialAlphaOffset;
            this.rotation.x += this.inertialBetaOffset;

            // Clamp pitch (floating free camera, no upside-down flip)
            if (this.rotation.x < this.lowerPitchLimit) this.rotation.x = this.lowerPitchLimit;
            if (this.rotation.x > this.upperPitchLimit) this.rotation.x = this.upperPitchLimit;

            // Movement in local camera axes
            if (!this._localDirection) {
                this._localDirection = BABYLON.Vector3.Zero();
                this._transformedDirection = BABYLON.Vector3.Zero();
            }

            const forward = this.getDirection(BABYLON.Axis.Z);
            const right = this.getDirection(BABYLON.Axis.X);
            const up = this.getDirection(BABYLON.Axis.Y);
            const isFlyMode = this._isFlyNavigationMode();
            const elevation = this.position.y;

            if (isFlyMode) {
                right.y = 0;

                if (this._flyKeepLevel) {
                    forward.y = 0;

                    if (forward.lengthSquared() > BABYLON.Epsilon) forward.normalize();
                    else forward.copyFromFloats(Math.sin(this.rotation.y), 0, Math.cos(this.rotation.y));
                }

                if (right.lengthSquared() > BABYLON.Epsilon) right.normalize();
                else right.copyFromFloats(Math.cos(this.rotation.y), 0, -Math.sin(this.rotation.y));

                up.copyFromFloats(0, 0, 0);
            }

            const speedScale = this.moveSpeed * this.zoomFactor;

            // Convert impulses into world displacement per-frame (dt)
            const moveF = (this.inertialMoveForward) * speedScale * dt; 
            const moveR = (this.inertialMoveRight) * speedScale * dt;
            const moveU = (this.inertialMoveUp) * speedScale * dt;

            const delta = forward.scale(moveF).addInPlace(right.scale(moveR)).addInPlace(up.scale(moveU));
            this.position.addInPlace(delta);
            if (isFlyMode && this._flyKeepLevel) this.position.y = elevation;

            // Decay inertia
            this.inertialAlphaOffset *= this.inertia;
            this.inertialBetaOffset *= this.inertia;
            this.inertialMoveForward *= this.inertia;
            this.inertialMoveRight *= this.inertia;
            this.inertialMoveUp *= this.inertia;

            if (Math.abs(this.inertialAlphaOffset) < BABYLON.Epsilon) this.inertialAlphaOffset = 0;
            if (Math.abs(this.inertialBetaOffset) < BABYLON.Epsilon) this.inertialBetaOffset = 0;

            if (Math.abs(this.inertialMoveForward) < BABYLON.Epsilon) this.inertialMoveForward = 0;
            if (Math.abs(this.inertialMoveRight) < BABYLON.Epsilon) this.inertialMoveRight = 0;
            if (Math.abs(this.inertialMoveUp) < BABYLON.Epsilon) this.inertialMoveUp = 0;
        }
        
        super._checkInputs.call(this);
        this._timeSinceFrameUpdate = BABYLON.Tools.Now;
    }

    public setInvertY(inverted: boolean) {
        this._invertY = inverted;
    }
    public setVelocitySensitivity(sensitivity: number) {
        this.velocitySensitivityPos = sensitivity;
    }

    public setRotationSensitivity(sensitivity: number) {
        this.rotationSensitivityPos = sensitivity;
    }

    public dollyIncrement(increment: number) {
        const velMul = this._velocityMultiplier();

        this.inertialMoveForward = increment * velMul;
    }
    public setRadiusVelocity(velocity: number) {
        const velMul = this._velocityMultiplier();

        this.inertialMoveForward = velocity * velMul / this.moveSpeed;
    }
    private _rotationMultiplier(): number {
        // -1 => 0.5x, 0 => 1x, +1 => 2x
        return Math.pow(2, this.rotationSensitivityPos);
    }

    private _velocityMultiplier(): number {
        // -1 => 0.5x, 0 => 1x, +1 => 5x
        const s = this.velocitySensitivityPos;
        return s < 0 ? Math.pow(2, s) : Math.pow(5, s);
    }
}

export default UniversalCamera;
